import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { SubscriptionSchedulerService } from 'src/app/reusables/subscription-scheduler/subscription-scheduler.service';
import { FullSubscriptionSchedule, ICvSubscriptionScheduleDataDates } from 'src/shared/models/subscriptions.model';
import { DashboardService } from '../../dashboard.service';
import { SelectValues } from './subscription-modal.model';
import { SubscriptionModalService, SubscriptionOptions } from './subscription-modal.service';

@Component({
    selector: 'cv-subscription-modal',
    templateUrl: './subscription-modal.component.html',
    styleUrls: ['./subscription-modal.component.scss']
})
export class SubscriptionModalComponent implements OnInit {

    /** Triggers the save schedule and will callback to the saved or saveFailed @Output */
    @Output() saved = new EventEmitter<FullSubscriptionSchedule>();
    @Output() saveFailed = new EventEmitter<void>();

    ownerEmail: string;
    initialized = false;
    isSaving = false;
    isLoading = false;
    dateFormat = CVAngularDateFormat;
    timeFormat = CVAngularTimeFormat;
    hoveredRecipient = -1;
    showDeleteButton = false;
    // Status for form validation
    schedule: ICvSubscriptionScheduleDataDates = null;
    newRecipients = "";

    // Frequency
    frequency = '';
    frequencies: SelectValues[] = [
        {
            value: 0,
            name: 'Daily',
            selected: false
        },
        {
            value: 1,
            name: 'Weekly',
            selected: false
        },
        {
            value: 2,
            name: 'Monthly',
            selected: false,
        }
    ];
    selectedFrequency: SelectValues;

    // Weekly Frequency
    daysOfWeek: SelectValues[] = [
        {
            value: 1,
            name: 'Sunday',
            shortName: 'Sun',
            selected: false
        },
        {
            value: 2,
            name: 'Monday',
            shortName: 'Mon',
            selected: false
        },
        {
            value: 3,
            name: 'Tuesday',
            shortName: 'Tue',
            selected: false
        },
        {
            value: 4,
            name: 'Wednesday',
            shortName: 'Wed',
            selected: false
        },
        {
            value: 5,
            name: 'Thursday',
            shortName: 'Thu',
            selected: false
        },
        {
            value: 6,
            name: 'Friday',
            shortName: 'Fri',
            selected: false
        },
        {
            value: 7,
            name: 'Saturday',
            shortName: 'Sat',
            selected: false
        },
    ];

    recurringDisplayValues: SelectValues[] = [
        {
            value: 0,
            name: 'mins',
            selected: false,
        },
        {
            value: 1,
            name: 'hours',
            selected: false,
        },
        {
            value: 2,
            name: 'days',
            selected: false,
        },
    ];

    // Monthly Frequency
    daysOfMonth: SelectValues[] = [];
    daysOfMonthSelected: SelectValues[] = [];

    // Repeat Hourly
    repeatHourlySelected = false;

    // Chip Options
    addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    // Email Error
    emailError = '';

    emailForm!: UntypedFormGroup;

    recipients: any[] = [];

    private modalData: SubscriptionOptions;

    constructor(
        public activeModal: NgbActiveModal,
        private cvLocalizationService: CVLocalizationService,
        private dashboardService: DashboardService,
        private service: SubscriptionModalService,
        private schedulerService: SubscriptionSchedulerService,
    ) {
        this.modalData = this.service._getModalData();

        this.selectedFrequency = this.frequencies[0];

        for (let i = 1; i <= 31; i++) {
            const displayName = (i !== 31) ? i.toString() : 'Last';

            this.daysOfMonth.push({
                id: i,
                value: i,
                selected: false,
                name: displayName,
            });
        }
    }

    async ngOnInit(): Promise<void> {
        this.emailForm = new UntypedFormGroup({
            email: new UntypedFormControl('', [Validators.email, Validators.required])
        });

        this.resetScheduleModel();
        this.ownerEmail = await this.schedulerService.getSubscriptionOwner();

        if (!this.modalData.subscriptionTitle)
            this.modalData.subscriptionTitle = "Subscription";

        if (this.modalData.subscriptionId === -1)
            this.modalData.subscriptionId = null;

        this.schedule.subscriptionId = this.modalData.subscriptionId;

        if (this.modalData.existingSchedule !== null && this.modalData.existingSchedule.ReportSubscriptionID !== -1) {
            await this.applyExistingSchedule();
        } else {
            await this.getSubscription();
        }

        this.schedule.recipients.forEach(email => {
            this.recipients.push({text: email});
        });

        await this.cvLocalizationService.GetSection('Subscription_Modal');
        await this.cvLocalizationService.GetSection('Objectives');

        this.frequency = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Frequency');

        this.initialized = true;
    }

    get email() { return this.emailForm.get('email'); }

    getLocalizedObjectivesText(option: string) {
        return this.cvLocalizationService.getCachedText('Objectives', option);
    }

    resetScheduleModel() {
        this.schedule = {
            scheduleID: null, // May not exist if it is a new subscription.
            reportType: 0,
            relatedKey: this.modalData.dashboardKey,
            relatedID: null,
            startDate: new Date(),
            endDate: null,
            exportType: 1,
            frequency: 1,
            daysOfWeek: [],
            daysOfMonth: [],
            weeksOfMonth: [],
            firstExportTime: new Date(),
            extraExportTimes: [],
            recipients: [],
            sendToMe: true,
            repeat: true,
            repeatOptions: {
                isRepeat: true,
                repeatValue: 1, // Default to one hour, but the minimum is 15 minutes
                repeatType: 2, // Days
                repeatEnd: moment().endOf("day").toDate(), // 11:59:59pm today
            }
        };
    }

    onFrequencyChange() {
        this.schedule.daysOfMonth = [];
        this.schedule.daysOfWeek = [];

        this.daysOfWeek.forEach(day => day.selected = false);
        this.daysOfMonthSelected = [];

        if (this.selectedFrequency.value === 0) {
            this.schedule.frequency = 1;
            this.schedule.repeat = true;
            this.schedule.repeatOptions = {
                isRepeat: true,
                repeatValue: 1,
                repeatType: 2
            };
        } else {
            this.schedule.frequency = this.selectedFrequency.value;
            this.schedule.repeat = false;
            this.schedule.repeatOptions.isRepeat = false;
            this.schedule.repeatOptions.repeatType = 1;
        }
    }

    onSelectedDaysChange() {
        this.schedule.daysOfWeek = this.daysOfWeek.map(x => x.selected);
    }

    onChangeSelectedDOM() {
        this.schedule.daysOfMonth = this.daysOfMonth.map(x => x.selected);
    }

    checkSelectedWeeks(): boolean {
        let selected = true;
        this.daysOfWeek.forEach(DOW => {
            if (DOW.selected) {
                selected = false;
            }
        });
        return selected;
    }

    async getSubscription() {
        if(this.schedule.relatedID == null)
            this.schedule.relatedID = this.modalData.relatedId;

        if (this.schedule.relatedKey == null)
            this.schedule.relatedKey = this.modalData.dashboardKey;

        //Try to retrieve a subscription. If one doesn't exist, an empty object with an ID of -1 will be returned.
        if (this.modalData.relatedId && this.modalData.dashboardKey) {
            this.isLoading = true;

            // This factory function gets a combination object containing both the ReportSubscription and ReportSubscriptionSchedule
            const response =
                await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(this.modalData.dashboardKey, this.modalData.relatedId);

            if (response.ReportSubscription.ReportSubscriptionID === -1) {
                this.isLoading = false;
                return;
            }

            this.showDeleteButton = true;

            this.schedule.sendToMe = response.ReportSubscription.SendToOwner;
            this.schedule.title = response.ReportSubscription.ReportSubscriptionName;
            this.schedule.folder = response.ReportSubscription.Folder;
            this.schedule.subscriptionId = response.ReportSubscription.ReportSubscriptionID;
            this.schedule.reportType = response.ReportSubscription.ReportType;
            this.schedule.endDate = this.modalData.embedMode ? moment(this.modalData.existingSchedule.EndDate).toDate()
                : moment(response.ReportSubscription.EndDate).toDate();
            this.schedule.exportType = response.ReportSubscription.ExportType;
            if (response.ReportSubscriptionSchedule != null) {
                this.schedule.startDate = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.schedule.frequency = response.ReportSubscriptionSchedule.ScheduleType;
                this.schedule.firstExportTime = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.schedule.extraExportTimes = (response.ReportSubscriptionSchedule.ExtraExportTimes !== undefined)
                    ? response.ReportSubscriptionSchedule.ExtraExportTimes.map(d => moment(d).toDate()) : [];
            } else {
                this.schedule.startDate = new Date();
                this.schedule.frequency = 1;
                this.schedule.firstExportTime = new Date();
                this.schedule.extraExportTimes = [];
            }
            this.schedule.daysOfWeek = [];
            this.schedule.daysOfMonth = [];
            this.schedule.weeksOfMonth = [];
            this.schedule.recipients = response.ReportSubscription.RecipientEmails;

            // If sendToMe is checked, remove the display value.
            // This is for cosmetic purposes. That way we don't have to update the checkbox state or recipients list state based on the others' state (is checked, contains owner's email, etc).
            // As long as the checkbox is checked, the controller will keep the owner's email in the recipients.
            if (this.schedule.sendToMe && this.ownerEmail !== undefined) {
                const cleanedOwnerEmailChars = this.ownerEmail.split('');
                ['(', ')'].forEach(c => {
                    if (cleanedOwnerEmailChars.indexOf(c) > -1)
                        cleanedOwnerEmailChars.splice(cleanedOwnerEmailChars.indexOf(c), 1);
                });

                const cleanedOwnerEmail = cleanedOwnerEmailChars.join('');
                const recipientOwnerPositionIndex = this.schedule.recipients.indexOf(cleanedOwnerEmail);
                if (recipientOwnerPositionIndex > -1)
                    this.schedule.recipients.splice(recipientOwnerPositionIndex, 1);
            }

            // If repeat is checked, it uses the given values. Otherwise, it uses blank values.
            if (response.ReportSubscriptionSchedule
                && response.ReportSubscriptionSchedule.RecurringMinutes > 0
                && response.ReportSubscriptionSchedule.RecurringDisplayType != null
                && response.ReportSubscriptionSchedule.EndDateTime != null) {
                this.schedule.repeatOptions.isRepeat = true;
                this.schedule.repeatOptions.repeatType = response.ReportSubscriptionSchedule.RecurringDisplayType;
                // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
                if (this.schedule.repeatOptions.repeatType === 1) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 60) || 0;
                } else if (this.schedule.repeatOptions.repeatType === 2) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 1440) || 0;
                } else {
                    this.schedule.repeatOptions.repeatValue = response.ReportSubscriptionSchedule.RecurringMinutes;
                }
                // This is done to avoid UTC to Local (in the endpoint) getting confused with daylight saving
                const endDateTime = moment(response.ReportSubscriptionSchedule.EndDateTime).toDate();
                const repeatEnd = moment().seconds(0)
                    .hours(endDateTime.getHours())
                    .minutes(endDateTime.getMinutes());
                this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
            } else {
                this.schedule.repeatOptions.isRepeat = false;
                this.schedule.repeatOptions.repeatValue = 1;
                this.schedule.repeatOptions.repeatType = 2; // days
                this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
            }

            // Map the multi-select values to the corresponding display model objects.
            // Convert from one int to a list of enum-ints (see enums in framework for highest int value)
            // daysOfWeek
            this.schedule.daysOfWeek = [];
            this.daysOfMonthSelected = [];
            if (response.ReportSubscriptionSchedule) {
                const dowLimit = Math.pow(2, 7);
                for (let i = 1, n = 0; i < dowLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfWeek & i) === i) {
                        this.schedule.daysOfWeek[n] = true;
                        this.daysOfWeek[n].selected = true;
                    } else {
                        this.schedule.daysOfWeek[n] = false;
                        this.daysOfWeek[n].selected = false;
                    }
                }
                this.daysOfWeek = this.daysOfWeek.slice();

                // daysOfMonth
                const domLimit = Math.pow(2, 31);
                for (let i = 1, n = 0; i < domLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfMonth & i) === i) {
                        this.schedule.daysOfMonth[n] = true;
                        this.daysOfMonthSelected.push(this.daysOfMonth[n]);
                        this.daysOfMonth[n].selected = true;
                    } else {
                        this.schedule.daysOfMonth[n] = false;
                        this.daysOfMonth[n].selected = false;
                    }
                }
            }

            this.selectedFrequency = this.frequencies.find((frequency) => frequency.value === this.schedule.frequency);
            this.isLoading = false;
        }
    }

    //#region Existing Schedule Handlers
    async applyExistingSchedule() {
        this.isLoading = true;

        const response = await this.schedulerService.getSubscriptionSchedule(
            this.modalData.existingSchedule.ReportSubscriptionID,
            this.modalData.existingSchedule.RelatedID,
            this.modalData.existingSchedule.RelatedKey);

        this.schedule.subscriptionId = this.modalData.existingSchedule.ReportSubscriptionID;
        this.schedule.scheduleID = response.ReportSubscriptionScheduleID;
        this.schedule.relatedID = this.modalData.relatedId;
        this.schedule.startDate = moment(response.StartDateTime).toDate();
        this.schedule.endDate = (response.EndDate != null) ? moment(response.EndDate).toDate()
            : moment(this.modalData.existingSchedule.EndDate).toDate();
        this.schedule.exportType = (this.modalData.existingSchedule.ExportType == null) ? this.schedule.exportType
            : this.modalData.existingSchedule.ExportType;
        this.schedule.frequency = response.ScheduleType;
        this.schedule.daysOfWeek = [];
        this.schedule.daysOfMonth = [];
        this.schedule.weeksOfMonth = [];
        this.schedule.firstExportTime = moment(response.StartDateTime).toDate();
        this.schedule.extraExportTimes = (response.ExtraExportTimes != null) ? response.ExtraExportTimes.map(d => moment(d).toDate()) : [];
        this.schedule.recipients = (this.modalData.existingSchedule.RecipientEmails != null)
            ? [...this.modalData.existingSchedule.RecipientEmails]
            : this.modalData.existingSchedule.Recipients?.map(r => r.EmailAddress);
        this.schedule.sendToMe = this.schedule.recipients.includes(this.ownerEmail);

        // If sendToMe is checked, remove the display value.
        // This is for cosmetic purposes. That way we don't have to update the checkbox state or recipients list state based on the others' state (is checked, contains owner's email, etc).
        // As long as the checkbox is checked, the controller will keep the owner's email in the recipients.
        if(this.schedule.sendToMe) {
            const recipientOwnerPositionIndex = this.schedule.recipients.indexOf(this.ownerEmail.replace('(', '').replace(')','')); // "(e@mail.com)" => "e@mail.com"
            if(recipientOwnerPositionIndex > -1)
                this.schedule.recipients.splice(recipientOwnerPositionIndex, 1);
        }

        // Repeat Options
        if (response.RecurringMinutes > 0) {
            this.schedule.repeat = true;
            this.schedule.repeatOptions.isRepeat = true;
            this.schedule.repeatOptions.repeatType = response.RecurringDisplayType;
            // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
            if (this.schedule.repeatOptions.repeatType === 1) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 60) || 0;
            } else if (this.schedule.repeatOptions.repeatType === 2) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 1440) || 0;
            } else {
                this.schedule.repeatOptions.repeatValue = response.RecurringMinutes;
            }
            const endDateTime = moment(response.EndDateTime).toDate();
            const repeatEnd = moment().seconds(0)
                .hours(endDateTime.getHours())
                .minutes(endDateTime.getMinutes());
            this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
        } else {
            this.schedule.repeatOptions.isRepeat = false;
            this.schedule.repeatOptions.repeatValue = 1;
            this.schedule.repeatOptions.repeatType = 2; // days
            this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
        }

        // Map the multi-select values to the corresponding display model objects.
        // Convert from one int to a list of enum-ints (see enums in framework for highest int value)
        // daysOfWeek
        this.schedule.daysOfWeek = [];
        const dowLimit = Math.pow(2, 7);
        for (let i = 1, n = 0; i < dowLimit; i *= 2, n++) {
            if ((response.DaysOfWeek & i) === i) {
                this.schedule.daysOfWeek[n] = true;
                this.daysOfWeek[n].selected = true;
            } else {
                this.schedule.daysOfWeek[n] = false;
                this.daysOfWeek[n].selected = false;
            }
        }
        this.daysOfWeek = this.daysOfWeek.slice();

        // daysOfMonth
        this.daysOfMonthSelected = [];
        const domLimit = Math.pow(2, 31);
        for (let i = 1, n = 0; i < domLimit; i *= 2, n++) {
            if ((response.DaysOfMonth & i) === i) {
                this.schedule.daysOfMonth[n] = true;
                this.daysOfMonthSelected.push(this.daysOfMonth[n]);
                this.daysOfMonth[n].selected = true;
            } else {
                this.schedule.daysOfMonth[n] = false;
                this.daysOfMonth[n].selected = false;
            }
        }

        if (this.schedule.repeatOptions.isRepeat && this.schedule.repeatOptions.repeatType === 2) {
            this.selectedFrequency = this.frequencies.find(frequency => frequency.value === 0);
        } else {
            this.selectedFrequency = this.frequencies.find(frequency => frequency.value === this.schedule.frequency);
            this.repeatHourlySelected = this.schedule.repeatOptions.isRepeat && this.schedule.repeatOptions.repeatType === 1;
        }

        this.schedule = this.schedulerService.objectifyTheScheduleDates(this.schedule);

        this.isLoading = false;
    }

    onRepeatHourlyChanged() {
        if (this.repeatHourlySelected) {
            this.schedule.repeat = true;
            this.schedule.repeatOptions = {
                isRepeat: true,
                repeatType: 1,
                repeatValue: 1
            };
        } else {
            this.schedule.repeat = false;
            this.schedule.repeatOptions = {
                isRepeat: false,
                repeatType: 2
            };
        }
    }

    getRepeatHourlyText(): string {
        const repeatHourlyText = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Repeat', 'Repeat hourly');
        return repeatHourlyText;
    }

    getSendToMeText() {
        const sendToMe = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Send_To_Me', 'Send to me');
        return sendToMe;
    }

    add(form: FormGroupDirective): void {
        const email = this.emailForm.get('email');
        if (email.valid) {
            if (!this.schedule.recipients.includes(email.value)) {
                this.schedule.recipients.push(email.value);
                this.recipients.push({text: email.value});
                this.recipients = this.recipients.slice();
            }
            this.emailForm.reset();
            form.resetForm();
        }
    }

    remove(email: any) {
        const index = this.schedule.recipients.indexOf(email['text']);

        if (index >= 0) {
            this.schedule.recipients.splice(index, 1);
        }
    }

    cancel() {
        this.resetScheduleModel();
        this.activeModal.dismiss();
    }

    async save() {
        if (this.isSaving)
            return;

        this.isLoading = true;

        if (this.selectedFrequency.value === 0) {
            this.daysOfWeek.forEach(day => day.selected = true);
            this.onSelectedDaysChange();
        }

        if (!this.modalData.embedMode){
            this.schedule.reportType = this.modalData.reportType;
            if( this.schedule.description == null)
                this.schedule.description = '';

            //When not in embedMode, it's assumed that the title needs to be manually set. If this.title is defined, use it. Otherwise, use a default value based on report type.
            if(this.modalData.subscriptionTitle != null) {
                this.schedule.title = this.modalData.subscriptionTitle;
            } else {
                switch (this.modalData.reportType) { //Based on ReportType enum in framework.
                    case 0: //Dashboard
                        this.schedule.title = "Dashboard Subscription";
                        break;
                    case 1: //Module
                        this.schedule.title = "Module Subscription";
                        break;
                    default:
                        break;
                }
            }

            if (this.schedule.subscriptionId == null) {
                try
                {
                    const response = await this.schedulerService.createSubscriptionSchedule(this.schedule);

                    if (this.modalData.reportType === 0) {
                        // update dashboard slideout (for the icon)
                        this.dashboardService.availableDashboards.find(dash => {
                            if (dash.DashboardKey === this.schedule.relatedKey) {
                                dash.IsSubscribed = true;
                            }
                        });

                        this.dashboardService.availableDashboardsChanged.emit();
                    }

                    this.activeModal.close();
                } catch (err) {
                    this.isSaving = false;
                    console.error('[CreateSubscriptionSchedule] Failed to save new subscription.', err);
                    err = err.data || err;
                    let errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            } else {
                try
                {
                    const response = await this.schedulerService.updateSubscriptionSchedule(this.schedule);
                    this.activeModal.close();
                } catch (err) {
                    console.error("Error updating subscription schedule", err);
                    err = err.data || err;
                    let errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            }
        }

        const newSubSchedule =
            await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(this.schedule.relatedKey, this.schedule.relatedID);
        this.isLoading = false;
        this.saved.emit(newSubSchedule);
    }
}
